/**
 * countryConfig.js — single source of truth for branding, palette,
 * map defaults, dial code, default dispatchers, etc.
 *
 * Strategy:
 *   - On boot we kick off ONE fetch to /api/country-config and cache it.
 *   - Until the fetch resolves, components fall back to the bundled
 *     Mali defaults (so we never render an empty-themed page even if
 *     the network is offline at boot).
 *   - `applyPalette()` writes the palette to CSS custom properties
 *     (`--country-primary`, `--country-accent`, etc.) which the
 *     Tailwind theme reads at runtime — meaning a different country's
 *     palette redraws every component without a code change.
 */

import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Bundled Mali fallback so the very-first paint (before /api/country-config
// resolves) still themes correctly. Kept identical to /app/backend/countries/ml.json.
export const MALI_FALLBACK = {
  code: "ML",
  name: "Mali",
  brand_name: "SOS Mali",
  tagline: "Plateforme citoyenne d'alertes d'urgence",
  dial_code: "+223",
  timezone: "UTC",
  timezone_label: "Bamako",
  default_locale: "fr",
  locales: ["fr", "en"],
  palette: {
    primary:   "#CE1126",
    accent:    "#FCD116",
    secondary: "#009639",
    ink:       "#0A1128",
  },
  flag_stripes: [
    { name: "green",  color: "#009639" },
    { name: "yellow", color: "#FCD116" },
    { name: "red",    color: "#CE1126" },
  ],
  map: { center_lng: -2.5, center_lat: 17.5, initial_zoom: 4.6 },
  default_dispatchers: {
    Medical: { phone: "15",  email: "", label: "SAMU"     },
    Fire:    { phone: "18",  email: "", label: "Pompiers" },
    Police:  { phone: "17",  email: "", label: "Police"   },
    FAMA:    { phone: "144", email: "", label: "FAMA"     },
  },
};

let _cached = { ...MALI_FALLBACK };
let _loadPromise = null;
const _listeners = new Set();

const _notify = () => {
  for (const fn of _listeners) {
    try { fn(_cached); } catch (_e) { /* listener crash must not break others */ }
  }
};

export function applyPalette(cfg) {
  if (typeof document === "undefined") return;
  const p = cfg?.palette || {};
  const root = document.documentElement;
  // New, country-agnostic tokens. Tailwind picks these up via CSS vars.
  if (p.primary)   root.style.setProperty("--country-primary", p.primary);
  if (p.accent)    root.style.setProperty("--country-accent", p.accent);
  if (p.secondary) root.style.setProperty("--country-secondary", p.secondary);
  if (p.ink)       root.style.setProperty("--country-ink", p.ink);
  // Back-compat: also overwrite the legacy `--mali-*` vars so every
  // existing `bg-mali-red`-style classname keeps working unchanged.
  if (p.primary)   root.style.setProperty("--mali-red", p.primary);
  if (p.accent)    root.style.setProperty("--mali-yellow", p.accent);
  if (p.secondary) root.style.setProperty("--mali-green", p.secondary);
  if (p.ink)       root.style.setProperty("--mali-navy", p.ink);
}

export function getCountryConfig() {
  return _cached;
}

export function loadCountryConfig() {
  if (_loadPromise) return _loadPromise;
  _loadPromise = (async () => {
    try {
      const { data } = await axios.get(`${BACKEND}/api/country-config`, { timeout: 8000 });
      if (data && data.code) {
        _cached = { ...MALI_FALLBACK, ...data };
        applyPalette(_cached);
        _notify();
      }
    } catch (_e) {
      // Stay on Mali fallback — never crash boot over a config fetch.
    }
    return _cached;
  })();
  return _loadPromise;
}

export function subscribeCountryConfig(fn) {
  _listeners.add(fn);
  // Push the current value immediately so late subscribers don't miss
  // an already-resolved fetch.
  try { fn(_cached); } catch (_e) { /* swallow */ }
  return () => _listeners.delete(fn);
}

// Eager: apply the bundled palette on import so the first paint sees
// the right CSS vars even before /api/country-config returns.
applyPalette(_cached);
// Kick off the real fetch.
loadCountryConfig();
